import { z } from "zod/v4";
import { ORPCError, os } from "@orpc/server";
import { zql } from "../../zero/schema";
import { withZeroTransaction } from "../../zero/utils";
import { createInsertSchema } from "../../utils/sql";
import { accessDenied } from "@corpuscomplete/core/utils";
import { food, foodMeta, foodServing } from "../food/food.sql";
import { brandedFood, brandedFoodServingNutrient } from "./branded-food.sql";
import { Unit } from "@corpuscomplete/core/common/unit";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { Food } from "../food";
import { FoodSearch } from "../food-search";
import { OpenFoodFacts } from "../external/openfoodfacts";
import { Icon } from "@corpuscomplete/core/common/icon";
import { Barcode } from "@corpuscomplete/core/common/barcode";
import { OpenFoodFactsBarcodeIngestion } from "../food-import/open-food-facts-barcode-ingestion";
import { and, desc, eq, isNull } from "drizzle-orm";
import type { Transaction } from "@rocicorp/zero";
import type { Actor, UserActor } from "@corpuscomplete/core/actor";
import type { Db } from "../../drizzle";
import { externalFoodCollection } from "../external/external.sql";
import { FoodCategory } from "../food-category";

/**
 * Branded-food namespace boundary for barcode lookup and OFF ingestion.
 *
 * Invariants:
 * - Barcode values must pass canonical normalization before lookup/ingest.
 * - Ingest path acquires per-collection advisory locks to prevent duplicates.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace BrandedFood {
  export type BarcodeLookupFailureReason = OpenFoodFacts.BarcodeLookupFailureReason;
  export type BarcodeLookupResponse = {
    item: Food.Item;
    source: "existing" | "ingested";
  };

  type RouterContext = {
    db: Db;
    actor: UserActor;
    offBarcodeLookup?: OpenFoodFactsBarcodeIngestion.BarcodeLookup;
    getOffTaxonomyGraph?: () => Promise<OpenFoodFacts.CategoryGraph.Snapshot | null>;
  };
  const base = os.$context<RouterContext>();

  export const router = {
    barcodeLookup: base
      .input(
        z.object({
          code: z.string().min(1),
        }),
      )
      .handler(async ({ context, input }): Promise<BarcodeLookupResponse> => {
        const parsedCode = Barcode.parse(input.code);
        if (!parsedCode.ok) {
          throw new ORPCError("NOT_FOUND", {
            message: "No match found",
          });
        }

        const existing = await lookupByBarcode(context.db, context.actor, {
          code: parsedCode.code,
        });
        if (existing) {
          return {
            item: existing,
            source: "existing",
          };
        }

        const ingestResult = await OpenFoodFactsBarcodeIngestion.ingestByBarcode({
          db: context.db,
          actor: context.actor,
          normalizedCode: parsedCode.code,
          offLookup: context.offBarcodeLookup,
          getOffTaxonomyGraph: context.getOffTaxonomyGraph,
          createBrandedFood: createFn,
          lookupByBarcode,
        });
        if (!ingestResult.item) {
          throw new ORPCError("NOT_FOUND", {
            message: "No match found",
          });
        }

        return {
          item: ingestResult.item,
          source: "ingested",
        };
      }),
  };

  export async function lookupByBarcode(
    db: Db,
    actor: UserActor,
    params: { code: string },
  ): Promise<Food.Item | null> {
    const parsedCode = Barcode.parse(params.code);
    if (!parsedCode.ok) {
      return null;
    }
    const normalizedCode = parsedCode.code;

    const [offCollection] = await db
      .select({ id: externalFoodCollection.id })
      .from(externalFoodCollection)
      .where(eq(externalFoodCollection.slug, OpenFoodFacts.slug))
      .limit(1)
      .execute();
    if (!offCollection) {
      return null;
    }

    const [existing] = await db
      .select({ id: food.id })
      .from(foodMeta)
      .innerJoin(food, Food.currentVersionJoinCondition())
      .innerJoin(
        brandedFood,
        and(eq(brandedFood.id, food.id), eq(brandedFood.version, food.version)),
      )
      .where(
        and(
          eq(foodMeta.type, "branded"),
          eq(foodMeta.collectionId, offCollection.id),
          eq(foodMeta.discoverability, "listed"),
          isNull(foodMeta.timeArchived),
          eq(brandedFood.code, normalizedCode),
          Food.viewableCondition(actor),
        ),
      )
      .orderBy(desc(food.timeCreated))
      .limit(1)
      .execute();
    if (!existing) {
      return null;
    }

    const [foodInfo] = await db
      .select(Food.infoSelect())
      .from(food)
      .innerJoin(foodMeta, Food.currentVersionJoinCondition())
      .where(eq(foodMeta.id, existing.id))
      .limit(1)
      .execute();
    if (!foodInfo?.meta) {
      return null;
    }

    const defaultServings = await FoodSearch.getDefaultServingsForFoods(db, [foodInfo.id]);
    const serving = defaultServings.get(foodInfo.id);
    if (!serving) {
      return null;
    }

    return {
      amount: serving.defaultAmount,
      food: foodInfo,
      serving,
    };
  }

  export const createSchema = z
    .object({
      id: z.uuid(),
      collectionId: z.uuid(),
      visibility: z.enum(["public", "private"]),
      discoverability: z.enum(["listed", "unlisted"]),
      categoryIds: z.array(z.uuid()).optional().default([]),
      iconSlugs: z.array(z.string()).min(1).max(4).nullable().optional(),
    })
    .extend({
      modifiers: Food.modifierSchema.array(),
      brandedFood: createInsertSchema(brandedFood)
        .omit({
          id: true,
          version: true,
        })
        .extend({
          code: Barcode.schema.nullable(),
        }),
      serving: createInsertSchema(foodServing)
        .omit({
          foodId: true,
          foodVersion: true,
          nutrients: true,
          massQuantity: true,
          massUnitKey: true,
          volumeQuantity: true,
          volumeUnitKey: true,
          modifiers: true,
        })
        .extend({
          modifiers: Food.servingModifierSchema.array(),
          mass: z
            .object({
              unitKey: Unit.keySchema(["mass"]),
              quantity: z.number().positive().max(999_999),
            })
            .nullable(),
          volume: z
            .object({
              unitKey: Unit.keySchema(["volume"]),
              quantity: z.number().positive().max(999_999),
            })
            .nullable(),
          nutrients: createInsertSchema(brandedFoodServingNutrient)
            .omit({
              foodId: true,
              foodVersion: true,
              foodServingId: true,
            })
            .array()
            .min(4),
        }),
      external: z
        .object({
          id: z.string(),
          version: z.string(),
          importVersion: z.number().int().positive(),
          name: z.string(),
          variant: z.string().nullable().optional(),
        })
        .optional(),
    });
  export type Create = z.input<typeof createSchema>;

  export const createFn = async ({
    tx,
    actor,
    options,
    args,
  }: {
    tx: Transaction;
    actor: Actor;
    args: Create;
    options?: {
      skipCollectionPermissionCheck?: boolean;
    };
  }) => {
    const {
      serving: servingInput,
      external,
      brandedFood,
      categoryIds,
      ...food
    } = createSchema.parse(args);

    if (!options?.skipCollectionPermissionCheck) {
      const hasPermission = await Food.canAddToCollection(tx, actor, food.collectionId, "branded");
      if (!hasPermission) {
        accessDenied("You do not have permission to add a food to this collection");
      }
    }

    if (tx.location === "server") {
      await Icon.validateSlugs(tx.dbTransaction.wrappedTransaction, food.iconSlugs);
    }

    const units = await tx.run(zql.unit.where("type", "IN", ["mass", "volume"]));
    const timeNow = Temporal.Now.instant();
    const version = 1;
    await tx.mutate.foodMeta.insert({
      id: food.id,
      type: "branded",
      collectionId: food.collectionId,
      currentVersion: null,
      visibility: food.visibility,
      discoverability: food.discoverability,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await tx.mutate.food.insert({
      id: food.id,
      version,
      name: `${brandedFood.brandName} ${brandedFood.productName}`,
      timeCreated: timeNow,
      modifiers: food.modifiers ?? [],
      iconSlugs: food.iconSlugs ?? null,
    });
    await tx.mutate.brandedFood.insert({
      ...brandedFood,
      id: food.id,
      version,
    });

    if (external) {
      await tx.mutate.externalFoodIdentity.insert({
        foodId: food.id,
        collectionId: food.collectionId,
        externalId: external.id,
        externalVariant: external.variant ?? null,
      });
      await tx.mutate.externalFoodProvenance.insert({
        foodId: food.id,
        foodVersion: version,
        collectionId: food.collectionId,
        externalId: external.id,
        externalVersion: external.version,
        importVersion: external.importVersion,
        externalName: external.name,
      });
    }

    const nutrientLookup = await Nutrient.createIdLookupForKeys(tx);
    const nutrients = Nutrient.nutrientsToAmounts(servingInput.nutrients, nutrientLookup);
    const serving: Food.ServingInput = {
      ...servingInput,
      nutrients,
    };

    Food.assertNoDuplicateUnitServings([serving], units);

    const sortedServings = Food.sortServings(
      Food.addUnitConversionServings([serving], units),
      units,
    );
    await Promise.all(
      sortedServings.map((serving) =>
        tx.mutate.foodServing.insert({
          ...Food.servingInputToRow(serving),
          foodId: food.id,
          foodVersion: version,
        }),
      ),
    );

    await Promise.all(
      servingInput.nutrients.map(({ nutrientId, amount }) =>
        tx.mutate.brandedFoodServingNutrient.insert({
          foodId: food.id,
          foodVersion: version,
          foodServingId: serving.id,
          nutrientId,
          amount,
        }),
      ),
    );

    if (categoryIds.length > 0) {
      await FoodCategory.addAssociations(tx, {
        foodId: food.id,
        foodVersion: version,
        isACategoryIds: categoryIds,
      });
    }

    await tx.mutate.foodMeta.update({
      id: food.id,
      currentVersion: version,
    });
  };

  export const create = withZeroTransaction(createSchema, ({ tx, ctx, args }) =>
    createFn({ tx, actor: ctx.actor, args }),
  );
}
